import { TourConfig } from "@/types/tour"
import { standardOffset } from "./utils/floatingUIConfigs"
import { navigateTo } from "@/services/tourNavigationService"
import { ensureRoute } from "./utils/routeNavigation"

/**
 * Tour configuration for the Project Settings page
 */
export const projectSettingsTour: TourConfig = {
   id: "project_settings_tour",
   humanReadableName: "Project Settings Tour",
   waitForElements: false,
   skippable: true,
   options: {
      useModalOverlay: true,
      defaultStepOptions: {
         classes: "q-card q-dark-transition shepherd-theme-quasar",
         arrow: true,
         scrollTo: { behavior: "smooth", block: "center" },
      },
   },
   steps: [
      {
         id: "welcome",
         title: "Welcome to Project Settings",
         text: "This tour will guide you through the settings available for your project.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         beforeShowPromise: ensureRoute("ProjectSettings"),
      },
      {
         id: "holdback-row",
         title: "Holdback Percentage",
         text: `The holdback percentage sets how many visitors see your default site instead of an ezbot-optimized version. This creates a control group to measure ezbot's impact. A higher percentage gives better comparison data, but fewer optimized sessions and a lower overall impact. We recommend using 5-10% for a good balance.
`,
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#holdback-slider",
            on: "right",
         },
         floatingUIOptions: standardOffset,
         beforeShowPromise: ensureRoute("ProjectSettings"),
      },
      {
         id: "optimization-metric",
         title: "Optimization Metric",
         text: "We currently support a \"binary reward\" optimization strategy. Here's how it works: if a session gets any number of reward events, it's considered a conversion, regardless of how many reward events are received for that session or their value.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: async function () {
                  // Shepherd adds this method to the button context
                  await navigateTo("ProjectSettingsInstall")
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#optimization-metric-dropdown",
            on: "right",
         },
         floatingUIOptions: standardOffset,
         beforeShowPromise: ensureRoute("ProjectSettings"),
      },
      {
         id: "install-instructions",
         title: "Installation Instructions",
         text: "This tab provides all the instructions you need to integrate ezbot with your website. Here you'll find code snippets for installing the ezbot tracking script and SDK, as well as examples for sending custom rewards to track conversions and optimize your site's performance.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: async function () {
                  // Shepherd adds this method to the button context
                  await navigateTo("ProjectSettingsTours")
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#install-panel",
            on: "right",
         },
         scrollTo: false, // Disable automatic scrolling
         floatingUIOptions: standardOffset,
         beforeShowPromise: ensureRoute("ProjectSettingsInstall"),
      },
      {
         id: "tours-explanation",
         title: "Tour Management",
         text: "This tab lets you restart any onboarding tours you’ve already completed. You can reset a specific tour or all tours to see the guides again.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: async function () {
                  // Navigate back to the Settings tab using the tour navigation service
                  await navigateTo("ProjectSettings")

                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#tours-panel",
            on: "right",
         },
         floatingUIOptions: standardOffset,
         beforeShowPromise: ensureRoute("ProjectSettingsTours"),
      },
      {
         id: "tour-complete",
         text: "You've completed the Project Settings tour! You can now explore and configure your project settings as needed.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: async function () {
                  await navigateTo("ProjectSettings")
                  if (this.complete) return this.complete()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Complete",
            },
         ],
         floatingUIOptions: standardOffset,
         beforeShowPromise: ensureRoute("ProjectSettingsTours"),
      },
   ],
}
